import { AUTH_TYPES } from "./types";
import {
  confirmMailRequest,
  forgotPasswordRequest,
  signOut,
  resetPasswordRequest,
  signIn,
} from "../helpers/api/auth";
import { SUCCESS_RESPONSE } from "../helpers/codeResponses/codes";
import { fetchWithAuthorizationToken } from "../helpers/api/fetch";
import { SC, SCA, SCI, SCN, USC } from "./condominiumActions";

export const Login = (credentials) => async (dispatch) => {
  dispatch(authRequest());
  try {
    const signInStatus = await signIn(credentials);

    if (signInStatus?.data) {
      const { data } = await fetchWithAuthorizationToken(
        `/auth/check/token`,
        signInStatus.data.api_token
      );

      if (data) {
        const userData = {
          token: signInStatus.data.api_token,
          role: data.rol,
          property: data.rol === "property" ? data.id : null,
          updated: data.data.created_at !== data.data.updated_at,
          firstname: data.data.firstname,
          lastname: data.data.lastname,
          image: data.data.image,
          // HO-HU-37: cuenta "property" con mas de una propiedad vinculada.
          // Con 0-1 propiedades esto es siempre false/[] y todo el resto de
          // este flujo corre exactamente igual que antes.
          hasMultipleProperties: !!data.hasMultipleProperties,
          properties: data.properties || [],
        };

        sessionStorage.setItem(
          "lose",
          !(data.data.created_at !== data.data.updated_at)
        );

        if (data.rol === "property" && data.hasMultipleProperties) {
          // No se auto-selecciona ninguna condominio/property todavia: el
          // usuario elige una en el selector que muestra el Dashboard
          // (mismo patron que ya usa el manager con sus condominios). Sin
          // esto, cual de sus varias propiedades quedaria "seleccionada"
          // por defecto seria arbitrario.
        } else if (data.rol === "property") {
          dispatch(SC(data.condominium.condominium_id));
          dispatch(SCN(data.condominium.name));
          dispatch(SCA(data.condominium.address));
          dispatch(SCI(data.condominium.identification));

          const data4 = await fetchWithAuthorizationToken(
            `/condominium/config/${data.condominium.condominium_id}`,
            signInStatus.data.api_token
          );

          if (data4) {
            dispatch(setCurr(data4.data.currency_id));
          }
        } else if (data.rol === "safety") {
          dispatch(SC(data.condominium.condominium_id));
          dispatch(SCN(data.condominium.name));
          dispatch(SCA(data.condominium.address));
          dispatch(SCI(data.condominium.identification));
        } else if (data.rol === "manager" && data.condominiumsCount === 1) {
          // HO-HU-36: con un solo condominio, "elegi un condominio" no es una
          // decision -- se selecciona solo, con el mismo efecto que si el
          // usuario hubiera hecho click en el unico item de la lista del
          // Dashboard. Con 2+ condominios no se toca nada y el selector sigue
          // apareciendo igual que hoy.
          //
          // El optional chaining es a proposito: front y backend se despliegan
          // por separado, y contra un backend que todavia no manda
          // 'condominiums' esto no debe romper el login, solo no auto-
          // seleccionar.
          const condominium = data.condominiums?.[0];

          if (condominium) {
            dispatch(SC(condominium.condominium_id));
            dispatch(SCN(condominium.name));
            dispatch(SCA(condominium.address));
            dispatch(SCI(condominium.identification));

            // Misma resolucion de moneda que hace CondominiumList2 al
            // seleccionar a mano: sin esto la app queda sin `currency` y no
            // muestra la tasa de cambio hasta recargar. Va en try/catch
            // aparte porque un problema con la config del condominio no
            // deberia tumbar el login entero.
            try {
              const config = await fetchWithAuthorizationToken(
                `/condominium/config/${condominium.condominium_id}`,
                signInStatus.data.api_token
              );

              if (config) {
                dispatch(setCurr(config.data.currency_id));
              }
            } catch (error) {
              // El selector de moneda sigue disponible manualmente si esto falla
            }
          }
        }

        dispatch(authLoginSigninSuccess(userData));
      } else {
        dispatch(authFailure(signInStatus));
      }
    }
  } catch (error) {
    dispatch(authFailure(error));
  } finally {
    dispatch(cleanErrors());
  }
};

export const Logout = () => async (dispatch) => {
  dispatch(authRequest());
  try {
    const response = await signOut();

    if (response.code === SUCCESS_RESPONSE.OK) {
      dispatch(authLogout());
      dispatch(USC());
    } else {
      dispatch(authFailure(response));
    }
  } catch (error) {
    dispatch(authFailure(error));
  } finally {
    dispatch(cleanErrors());
  }
};

export const confirmMailLink = (email) => async (dispatch) => {
  dispatch(authRequest());

  try {
    const sent = await confirmMailRequest(email);
    if (sent.code === SUCCESS_RESPONSE.OK) {
      dispatch(authForgotPasswordRequestSuccess({ email }));
    } else {
      dispatch(authFailure(sent));
    }
  } catch (error) {
    dispatch(authFailure(error));
  } finally {
    dispatch(cleanErrors());
  }
};

export const forgotPassword = (email) => async (dispatch) => {
  dispatch(authRequest());

  try {
    const sent = await forgotPasswordRequest(email);
    if (sent.code === SUCCESS_RESPONSE.OK) {
      dispatch(authForgotPasswordRequestSuccess({ email }));
    } else {
      dispatch(authFailure(sent));
    }
  } catch (error) {
    dispatch(authFailure(error));
  } finally {
    dispatch(cleanErrors());
  }
};

export const resetPassword = (body) => async (dispatch) => {
  dispatch(authForgotPasswordRequest());

  try {
    const sent = await resetPasswordRequest(body);

    if (sent.code === SUCCESS_RESPONSE.OK) {
      dispatch(
        authResetPasswordRequestSuccess({
          email: body.email,
          changedPassword: true,
        })
      );
    } else {
      dispatch(authForgotPasswordRequestFailure(sent));
    }
  } catch (error) {
    dispatch(authForgotPasswordRequestFailure(error));
  } finally {
    dispatch(cleanErrors());
  }
};

export const updatedUserProfile = (payload) => async (dispatch) => {
  dispatch(changeProfile(payload));
};

export const setCurrency = (currency) => async (dispatch) => {
  dispatch(setCurr(currency));
};

export const authRequest = () => ({ type: AUTH_TYPES.AUTH_REQUEST });
export const authFailure = (errors) => ({
  type: AUTH_TYPES.AUTH_FAILURE,
  payload: errors,
});
export const userIsFetching = (payload) => ({
  type: AUTH_TYPES.USER_IS_FETCHING,
  payload: payload,
});

export const authLogout = () => ({ type: AUTH_TYPES.LOGOUT_SUCCESS });
export const cleanErrors = () => ({ type: AUTH_TYPES.CLEAN_ERRORS });
export const changeProfile = (payload) => ({
  type: AUTH_TYPES.CHANGE_PROFILE,
  payload,
});

/**
 * HO-ISSUE-119: refresca las propiedades vinculadas de la cuenta con lo que
 * devuelve /auth/check/token. Recibe el perfil completo y toma de ahi
 * properties y hasMultipleProperties.
 */
export const updateMyProperties = (profile) => ({
  type: AUTH_TYPES.UPDATE_MY_PROPERTIES,
  payload: {
    properties: profile?.properties || [],
    hasMultipleProperties: !!profile?.hasMultipleProperties,
  },
});

export const authLoginSigninSuccess = (payload) => ({
  type: AUTH_TYPES.LOGIN_SUCCESS,
  payload,
});
export const authForgotPasswordRequest = () => ({
  type: AUTH_TYPES.LOGIN_FORGOT_PASSWORD_REQUEST,
});
export const authForgotPasswordRequestSuccess = (payload) => ({
  type: AUTH_TYPES.LOGIN_FORGOT_PASSWORD_REQUEST_SUCCESS,
  payload,
});
export const authForgotPasswordRequestFailure = (error) => ({
  type: AUTH_TYPES.LOGIN_FORGOT_PASSWORD_REQUEST_FAILURE,
  payload: error,
});

export const authResetPasswordRequestSuccess = (payload) => ({
  type: AUTH_TYPES.LOGIN_RESET_PASSWORD_REQUEST_SUCCESS,
  payload,
});
export const setCurr = (payload) => ({
  type: AUTH_TYPES.SET_CURRENCY,
  payload,
});
